// src/components/managed-sources/useManagedSourcePoll.ts

import { useEffect, useMemo, useRef, useState } from "react";

import { ApiError } from "@/api/client";
import { getManagedSource } from "@/api/managedSources";
import type { ManagedSource } from "@/types/managedSources";

const POLL_INTERVAL_MS = 5_000;

/** ~5 minutes of polling at 5s. Past that the caller should stop hoping. */
const MAX_POLLS = 60;

/**
 * Statuses that mean "this source is not ours to read anymore" — either the
 * session lost access (401/403) or the row is gone (404). Retrying those is
 * pointless and, for 401, actively harmful: the API client already redirects
 * to /login on a failed refresh.
 */
const GONE_STATUSES = new Set([401, 403, 404]);

export type ManagedSourcePollResult = {
  /** Latest successfully fetched source, or null before the first success. */
  source: ManagedSource | null;
  /** True while polling is active (not stopped/capped/terminal). */
  polling: boolean;
  /** Set when polling stopped because the resource is gone or forbidden. */
  gone: boolean;
  /** True when polling stopped because the poll cap was hit. */
  cappedOut: boolean;
};

/**
 * Polls a single managed source until it reaches a caller-defined terminal
 * state. Deliberately narrower than the detail drawer's poll: one resource,
 * no run history, a hard cap, and an explicit "gone" outcome so a caller can
 * tell "still working" apart from "stop waiting".
 *
 * There is no leading call — the caller already holds the source it just
 * created, so the first fetch fires after one interval.
 */
export function useManagedSourcePoll(
  sourceId: string | null,
  opts: { enabled: boolean; isTerminal: (s: ManagedSource) => boolean },
): ManagedSourcePollResult {
  const { enabled, isTerminal } = opts;

  const [source, setSource] = useState<ManagedSource | null>(null);
  const [gone, setGone] = useState(false);
  const [cappedOut, setCappedOut] = useState(false);
  const [terminal, setTerminal] = useState(false);

  // Hidden tabs don't poll, so they don't burn cap budget either.
  const [visible, setVisible] = useState(
    () => document.visibilityState !== "hidden",
  );

  const pollCountRef = useRef(0);
  const inFlightRef = useRef(false);

  // Callers pass `isTerminal` inline, so it's a new function every render.
  // Holding it in a ref keeps the poll effect from tearing down each render.
  const isTerminalRef = useRef(isTerminal);
  useEffect(() => {
    isTerminalRef.current = isTerminal;
  });

  // A different source is a different poll: drop the previous result and
  // counters rather than letting them leak across identities.
  useEffect(() => {
    pollCountRef.current = 0;
    setSource(null);
    setGone(false);
    setCappedOut(false);
    setTerminal(false);
  }, [sourceId]);

  useEffect(() => {
    const onVisibilityChange = () =>
      setVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  const polling = enabled && sourceId !== null && !gone && !cappedOut && !terminal;

  useEffect(() => {
    if (!polling || !visible || !sourceId) return;

    let cancelled = false;

    const interval = setInterval(async () => {
      // A response slower than the interval must not stack up requests.
      if (inFlightRef.current) return;
      inFlightRef.current = true;

      const attempt = pollCountRef.current + 1;
      pollCountRef.current = attempt;

      let stopped = false;
      try {
        const next = await getManagedSource(sourceId);
        if (cancelled) return;
        setSource(next);
        if (isTerminalRef.current(next)) {
          setTerminal(true);
          stopped = true;
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && GONE_STATUSES.has(err.status)) {
          setGone(true);
          stopped = true;
        }
        // Anything else is transport noise: keep the last known source and
        // retry on the next tick. The attempt still counts toward the cap.
      } finally {
        inFlightRef.current = false;
        if (!cancelled && !stopped && attempt >= MAX_POLLS) {
          setCappedOut(true);
        }
      }
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [polling, visible, sourceId]);

  return useMemo(
    () => ({ source, polling, gone, cappedOut }),
    [source, polling, gone, cappedOut],
  );
}
